import { AnalyticsEventName } from './eventNames';

type AnalyticsParams = Record<string, string | number | boolean | null | undefined>;

let analyticsInstance: any = null;
let initAttempted = false;

function isFirebaseConfigured(): boolean {
  return !!(
    process.env.VUE_APP_FIREBASE_API_KEY &&
    process.env.VUE_APP_FIREBASE_PROJECT_ID &&
    process.env.VUE_APP_FIREBASE_APP_ID &&
    process.env.VUE_APP_FIREBASE_MEASUREMENT_ID
  );
}

/**
 * Lazily initialise Firebase Analytics when env is configured.
 */
export async function initAnalytics(): Promise<void> {
  if (initAttempted) {
    return;
  }
  initAttempted = true;

  if (!isFirebaseConfigured()) {
    return;
  }

  try {
    const { initializeApp, getApps } = await import('firebase/app');
    const { getAnalytics, isSupported } = await import('firebase/analytics');

    const supported = await isSupported();
    if (!supported) {
      return;
    }

    const config = {
      apiKey: process.env.VUE_APP_FIREBASE_API_KEY,
      authDomain: process.env.VUE_APP_FIREBASE_AUTH_DOMAIN,
      projectId: process.env.VUE_APP_FIREBASE_PROJECT_ID,
      appId: process.env.VUE_APP_FIREBASE_APP_ID,
      measurementId: process.env.VUE_APP_FIREBASE_MEASUREMENT_ID,
    };

    const app = getApps().length ? getApps()[0] : initializeApp(config);
    analyticsInstance = getAnalytics(app);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[analytics] Firebase init failed', e);
  }
}

export function sanitizeParams(params: AnalyticsParams = {}): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  let count = 0;
  Object.keys(params).forEach((key) => {
    if (count >= 25) {
      return;
    }
    const value = params[key];
    if (value == null) {
      return;
    }
    const name = String(key).slice(0, 40);
    if (typeof value === 'boolean') {
      out[name] = value ? 1 : 0;
    } else if (typeof value === 'number') {
      out[name] = value;
    } else {
      out[name] = String(value).slice(0, 100);
    }
    count += 1;
  });
  return out;
}

/**
 * Track a product analytics event. Falls back to console when Firebase is not configured.
 */
export async function trackEvent(
  eventName: AnalyticsEventName,
  params: AnalyticsParams = {},
): Promise<void> {
  const payload = sanitizeParams({ platform: 'web', ...params });

  try {
    await initAnalytics();
    if (analyticsInstance) {
      const { logEvent } = await import('firebase/analytics');
      logEvent(analyticsInstance, String(eventName).slice(0, 40), payload);
      return;
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[analytics] trackEvent failed', eventName, e);
  }

  // eslint-disable-next-line no-console
  console.info(`[ANALYTICS] ${eventName}`, payload);
}

export async function setAnalyticsUserId(userId: string | number | null | undefined): Promise<void> {
  if (userId == null) {
    return;
  }
  try {
    await initAnalytics();
    if (analyticsInstance) {
      const { setUserId } = await import('firebase/analytics');
      setUserId(analyticsInstance, String(userId));
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[analytics] setUserId failed', e);
  }
}

/** Test helper — reset lazy init state */
export function __resetAnalyticsForTests(): void {
  analyticsInstance = null;
  initAttempted = false;
}
